import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '../data/mockData';
import { useTemplate } from '../context/TemplateContext';
import { useApp } from '../context/AppContext';
import { aiService } from '../services/aiService';
import { taoApi } from '../services/taoApi';
import { StyleTemplate } from '../types/template';
import CategoryMenu from './CategoryMenu';
import TemplateGrid from './TemplateGrid';
import PhotoProcessingModal from './PhotoProcessingModal';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { addTemplate } = useTemplate();
  const { electron, isElectronMode, setPhotoProcessingMode, config } = useApp();
  const [activeCategory, setActiveCategory] = useState('all');
  const [templates, setTemplates] = useState<StyleTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<StyleTemplate | null>(null);

  // 从 config 中读取 aiProvider，默认为 'mock'
  const aiProvider = config?.aiProvider || 'mock';
  const isPrintMode = config?.canPrint === true;

  // 在开发环境下显示URL参数状态（可选）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('HomePage: URL parameters loaded:', {
        electron,
        isElectronMode,
        isPrintMode,
        aiProvider
      });
    }
  }, [electron, isElectronMode, isPrintMode, aiProvider]);

  // 设置AI服务提供商和TAO API基础URL
  useEffect(() => {
    aiService.setAiProvider(aiProvider);
    
    // 根据 aiProvider 判断是 tao-local 还是 tao-remote
    if (aiProvider === 'tao-local') {
      taoApi.setBaseUrl('local');
    } else if (aiProvider === 'tao-remote') {
      taoApi.setBaseUrl('remote');
    }
  }, [aiProvider]);

  // 加载模版数据
  const loadTemplates = useCallback(async (categoryId: string, pageNum: number, reset: boolean = false) => {
    setLoading(true);
    try {
      if (aiProvider === 'tao-local' || aiProvider === 'tao-remote') {
        // TAO API: 一次性返回所有模板，忽略分页参数
        const { items: newTemplates } = await aiService.getStyleTemplates({
          categoryId: 'all',
          page: 1,
          pageSize: 100
        });

        setTemplates(newTemplates);
        setHasMore(false); // TAO API 一次性返回所有模板
        
        // 将模板添加到全局上下文中
        newTemplates.forEach(template => addTemplate(template));
      } else {
        // 其他API: 支持分页
        const { items: newTemplates, hasMore: moreAvailable } = await aiService.getStyleTemplates({
          categoryId,
          page: pageNum,
          pageSize: 20
        });

        setTemplates(prev => reset ? newTemplates : [...prev, ...newTemplates]);
        setHasMore(!!moreAvailable);
        
        // 将模板添加到全局上下文中
        newTemplates.forEach(template => addTemplate(template));
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  }, [addTemplate, aiProvider]);

  // 切换分类
  const handleCategoryChange = useCallback((categoryId: string) => {
    if (categoryId === activeCategory) return;
    if (aiProvider === 'tao-local' || aiProvider === 'tao-remote') return; // TAO provider 不支持分类切换
    
    setActiveCategory(categoryId);
    setPage(1);
    setTemplates([]);
    setHasMore(true);
    loadTemplates(categoryId, 1, true);
  }, [activeCategory, loadTemplates, aiProvider]);

  // 加载更多
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && aiProvider === 'mock') {
      const nextPage = page + 1;
      setPage(nextPage);
      loadTemplates(activeCategory, nextPage);
    }
  }, [loading, hasMore, page, activeCategory, loadTemplates, aiProvider]);

  // 处理模版点击 - 制作同款
  const handleTemplateClick = useCallback((template: StyleTemplate) => {
    if (!isPrintMode) {
      // 非打印模式下直接进入 style-only 模式
      setPhotoProcessingMode('style-only');
      navigate(`/template/${template.id}`);
    } else {
      // 打印模式下显示选择对话框
      setSelectedTemplate(template);
      setIsModalOpen(true);
    }
  }, [isPrintMode, setPhotoProcessingMode, navigate]);

  // 处理照片处理方式选择
  const handleProcessingModeSelect = useCallback((mode: 'raster' | 'style-only') => {
    if (selectedTemplate) {
      // 保存选择的处理方式到全局状态
      setPhotoProcessingMode(mode);
      // console.log('选择的照片处理方式:', mode);
      // console.log('选择的模板:', selectedTemplate);

      // 导航到模板详情页
      navigate(`/template/${selectedTemplate.id}`);
    }
  }, [selectedTemplate, setPhotoProcessingMode, navigate]);

  // 关闭模态框
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  }, []);

  // 初始加载
  useEffect(() => {
    const initialLoad = async () => {
      setLoading(true);
      
      try {
        if (aiProvider === 'tao-local' || aiProvider === 'tao-remote') {
          // TAO API: 一次性加载所有模板
          const { items: newTemplates } = await aiService.getStyleTemplates({
            categoryId: 'all',
            page: 1,
            pageSize: 100
          });

          setTemplates(newTemplates);
          setHasMore(false);
          
          // 将模板添加到全局上下文中
          newTemplates.forEach(template => addTemplate(template));
        } else {
          // 其他API: 分页加载
          const { items: newTemplates, hasMore: moreAvailable } = await aiService.getStyleTemplates({
            categoryId: 'all',
            page: 1,
            pageSize: 20
          });

          setTemplates(newTemplates);
          setHasMore(!!moreAvailable);
          
          // 将模板添加到全局上下文中
          newTemplates.forEach(template => addTemplate(template));
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoading(false);
      }
    };

    // 确保AI服务提供商已设置后再加载
    const timer = setTimeout(() => {
      initialLoad();
    }, 100); // 给AI服务设置一点时间

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [aiProvider, addTemplate]); // 依赖aiProvider和addTemplate，当AI提供商变化时重新加载

  // 当使用TAO提供商时，不显示分类菜单
  const shouldShowCategoryMenu = aiProvider === 'mock';

  return (
    <div className="home-page">
      {/* 开发环境下显示URL参数状态 */}
      {process.env.NODE_ENV === 'development' && (electron || aiProvider !== 'unicom') && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: '#f0f0f0',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 1000,
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
        }}>
          <div><strong>URL参数:</strong></div>
          {electron && <div>electron: {electron}</div>}
          <div>aiProvider: {aiProvider}</div>
          <div><strong>模式:</strong></div>
          <div>Electron模式: {isElectronMode ? '开启' : '关闭'}</div>
          <div>打印模式: {isPrintMode ? '开启' : '关闭'}</div>
          <div>AI提供商: {aiProvider}</div>
        </div>
      )}
      
      <section className="title-section">
        <h1 className="title">AI风格化你的美照</h1>
        <p className="subtitle">选择一个模版, 快速制作专属于你的风格化照片</p>
      </section>

      {shouldShowCategoryMenu && (
        <CategoryMenu
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />
      )}
      
      <TemplateGrid
        templates={templates}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onTemplateClick={handleTemplateClick}
      />

      <PhotoProcessingModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelect={handleProcessingModeSelect}
      />
    </div>
  );
};

export default HomePage;
